/**
 * Closed & funded deals — Declined/Finished, Offer Rejected (NB / TU) and Funded live outside the Mirror's working groups,
 * but processors need them: a declined deal that comes back, and the original Funded deal when a (TU) application lands.
 * Search covers the WHOLE board plus the Declined Apps archive board (SF number, company, contact, broker email) — Monday
 * batch-moves Declined/Finished deals off the live board, which is how SF-39980 "vanished" (John, 2 Nov 2026).
 * The group tabs show the newest 50 per group.
 */
import { useEffect, useState } from "react";
import { Loader2, Search, Archive, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { mirrorClosed } from "@/lib/api";

const COL = { STATUS: "status", SF: "text_mkpn6xxt", EMAIL: "text_mkm837d5", CONTACT: "text_mkqecmwn", BROKER: "board_relation", OFFER_AMT: "numeric", DECL_REASON: "dropdown_mkm82kf4", DATE: "date4" };
const GROUP_CLS = {
  "Funded": "bg-emerald-100 text-emerald-800", "Declined/Finished": "bg-rose-100 text-rose-800",
  "Offer Rejected - New Business": "bg-amber-100 text-amber-800", "Offer Rejected - Top Up": "bg-amber-100 text-amber-800",
};
const money = (v) => (v ? `£${Number(String(v).replace(/[^\d.]/g, "")).toLocaleString("en-GB", { maximumFractionDigits: 0 })}` : "");

export function ClosedDeals({ query, onOpenItem, boardUrl }) {
  const [group, setGroup] = useState("funded");
  const [q, setQ] = useState(query || "");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (query && query.trim().length >= 2) setQ(query); }, [query]);
  useEffect(() => {
    let dead = false;
    const t = setTimeout(async () => {
      setBusy(true); setErr("");
      try {
        const r = await mirrorClosed(q.trim().length >= 2 ? { q: q.trim() } : { group });
        if (!dead) setData(r);
      } catch (e) { if (!dead) setErr(e.response?.data?.detail || e.message); } finally { if (!dead) setBusy(false); }
    }, q.trim().length >= 2 ? 350 : 0);
    return () => { dead = true; clearTimeout(t); };
  }, [q, group]);
  const rows = data?.rows || [];
  const searching = q.trim().length >= 2;
  return (
    <div className="border rounded-lg bg-white" data-testid="mirror-closed-panel">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b bg-slate-50 rounded-t-lg">
        <Archive className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-xs font-semibold text-slate-800">Closed & funded deals</span>
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the whole board — SF number, company, contact, broker email…"
            className="h-7 text-xs pl-7" data-testid="mirror-closed-search" />
        </div>
        {!searching && (data?.groups || []).map(g => (
          <button key={g.key} type="button" onClick={() => setGroup(g.key)}
            className={cn("text-[11px] px-2 py-0.5 rounded-full border transition-colors", group === g.key ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 hover:bg-slate-100")}
            data-testid={`mirror-closed-tab-${g.key}`}>{g.title}</button>
        ))}
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        <span className="text-[10px] text-slate-500 ml-auto" data-testid="mirror-closed-count">
          {searching ? `${rows.length} match${rows.length === 1 ? "" : "es"} across the board` : data ? `newest ${rows.length}${data.has_more ? " — search for older" : ""}` : ""}
        </span>
      </div>
      {err && <div className="text-xs text-red-700 px-3 py-2" data-testid="mirror-closed-error">{err}</div>}
      {!err && data && !rows.length && <div className="text-xs text-slate-500 px-3 py-3" data-testid="mirror-closed-empty">{searching ? `Nothing on the board matches "${q.trim()}".` : "Nothing here."}</div>}
      {rows.length > 0 && (
        <table className="w-full text-[11px]" data-testid="mirror-closed-table">
          <thead><tr className="text-left text-slate-500 border-b"><th className="px-3 py-1">Deal</th><th>Group · status</th><th>Contact</th><th>Broker</th><th>Offer / reason</th><th>Date</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b last:border-0 hover:bg-violet-50/40 cursor-pointer" onClick={() => onOpenItem(r.id)} data-testid={`mirror-closed-row-${r.id}`}>
                <td className="px-3 py-1.5 font-medium text-slate-800">{r.name}</td>
                <td><Badge variant="outline" className={cn("text-[10px] border-0 mr-1", GROUP_CLS[r.group] || "bg-slate-100 text-slate-700")}>{r.group}</Badge>
                  {r.board && <Badge variant="outline" className="text-[10px] border-violet-200 bg-violet-50 text-violet-700 mr-1" title={`Monday batch-moves Declined/Finished deals onto the ${r.board} board`} data-testid={`mirror-closed-board-${r.id}`}>{r.board} board</Badge>}
                  <span className="text-slate-600">{r.cols?.[COL.STATUS]}</span></td>
                <td className="text-slate-600">{r.cols?.[COL.CONTACT] || r.cols?.[COL.EMAIL] || ""}</td>
                <td className="text-slate-600">{r.cols?.[COL.BROKER] || ""}</td>
                <td className="text-slate-600">{money(r.cols?.[COL.OFFER_AMT]) || r.cols?.[COL.DECL_REASON] || ""}</td>
                <td className="text-slate-500 whitespace-nowrap">{r.cols?.[COL.DATE] || r.created_at}</td>
                <td className="pr-2 text-right"><Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={(e) => { e.stopPropagation(); onOpenItem(r.id); }} data-testid={`mirror-closed-open-${r.id}`}>Open</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {boardUrl && <div className="px-3 py-1.5 text-[10px] text-slate-400 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Opening a deal uses the same drawer as the live groups — notes, files, emails and status all work on closed deals too.{Object.keys(data?.archive_boards || {}).length > 0 && " Search also covers the Declined Apps board, where Monday archives Declined/Finished deals."}</div>}
    </div>
  );
}
